import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { GlassCard } from "@/components/common/GlassCard";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusPill } from "@/components/common/StatusPill";
import { LoadingState, EmptyState } from "@/components/common/States";
import { TraitBoxes } from "@/components/common/TraitBoxes";
import { PlayerSubNav } from "@/components/common/PlayerSubNav";
import { GameGradesCard } from "@/components/player/GameGradesCard";
import { ClipboardList, Plus, Sparkles, Layers, Target, X, Quote, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import { gradeLabel, GRADE_TONE_CLASS } from "@/lib/gradeScale";
import { cn } from "@/lib/utils";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

function useResolvedPlayerId() {
  const { user } = useAuth();
  const { playerId } = useParams();
  return playerId || user?.player_id;
}

const TRAIT_GROUPS = [
  {
    key: "general_traits",
    label: "General Traits",
    description: "10 macro evaluation traits applied to every prospect.",
    icon: Layers,
    color: "#22D3EE",
  },
  {
    key: "position_traits",
    label: "Position Traits",
    description: "Position-specific skills and technical attributes.",
    icon: Target,
    color: "#60A5FA",
  },
];

function avgScored(items) {
  if (!items) return null;
  const vals = items.map((t) => t.score || 0).filter((s) => s > 0);
  if (!vals.length) return null;
  return Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10;
}

export default function EvaluationPage() {
  useDocumentTitle("OmniDraftLab · Evaluation");
  const { user } = useAuth();
  const playerId = useResolvedPlayerId();
  const [evals, setEvals] = useState([]);
  const [active, setActive] = useState(null);
  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [headlineLoading, setHeadlineLoading] = useState(false);
  const [archetypes, setArchetypes] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [newStrength, setNewStrength] = useState("");
  const [newWeakness, setNewWeakness] = useState("");
  const isAdmin = user?.role === "admin";

  const load = async () => {
    if (!playerId) { setLoading(false); return; }
    setLoading(true);
    try {
      const [pRes, eRes] = await Promise.all([
        api.get(`/players/${playerId}`),
        api.get("/evaluations", { params: { player_id: playerId } }),
      ]);
      setPlayer(pRes.data);
      setEvals(eRes.data);
      setActive(eRes.data[0] || null);
      const cfg = await api.get("/evaluations/trait-config", { params: { position: pRes.data.position } });
      setArchetypes(cfg.data.archetypes || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [playerId]);

  const updateActive = async (patch) => {
    if (!active) return;
    try {
      const r = await api.patch(`/evaluations/${active.id}`, patch);
      setActive(r.data);
      setEvals((arr) => arr.map((e) => e.id === r.data.id ? r.data : e));
    } catch (e) { toast.error("Save failed"); }
  };

  const setTraitScore = (groupKey, idx, score) => {
    const items = (active[groupKey] || []).map((t, i) => i === idx ? { ...t, score } : t);
    updateActive({ [groupKey]: items });
  };

  const setTraitNoteLocal = (groupKey, idx, note) => {
    setActive((prev) => prev ? {
      ...prev,
      [groupKey]: (prev[groupKey] || []).map((t, i) => i === idx ? { ...t, note } : t),
    } : prev);
  };

  const commitTraitNote = (groupKey) => {
    if (!active) return;
    updateActive({ [groupKey]: active[groupKey] || [] });
  };

  const setOverallOverride = (val) => {
    updateActive({ overall_grade: val, overall_grade_overridden: true });
  };
  const clearOverride = () => {
    if (!active) return;
    api.patch(`/evaluations/${active.id}`, { overall_grade_overridden: false }).then((r) => {
      setActive(r.data);
      setEvals((arr) => arr.map((e) => e.id === r.data.id ? r.data : e));
      toast.success("Override removed — using recommended grade");
    });
  };

  const generate = async () => {
    if (!active) return;
    setGenerating(true);
    try {
      const r = await api.post(`/evaluations/${active.id}/ai-summary`);
      setActive(r.data);
      setEvals((arr) => arr.map((e) => e.id === r.data.id ? r.data : e));
      toast.success("AI scouting report generated");
      const pRes = await api.get(`/players/${playerId}`);
      setPlayer(pRes.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate report");
    } finally { setGenerating(false); }
  };

  const generateHeadline = async () => {
    if (!active) return;
    setHeadlineLoading(true);
    try {
      const r = await api.post(`/evaluations/${active.id}/ai-headline`);
      setActive(r.data);
      setEvals((arr) => arr.map((e) => e.id === r.data.id ? r.data : e));
      toast.success("Headline generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate headline");
    } finally { setHeadlineLoading(false); }
  };

  const addNote = async () => {
    if (!active || !noteText.trim()) return;
    const r = await api.post(`/evaluations/${active.id}/notes`, { text: noteText });
    setActive(r.data);
    setNoteText("");
    toast.success("Note added");
  };

  const addStrength = () => {
    if (!newStrength.trim()) return;
    const items = [...(active.strengths || []), newStrength.trim()];
    updateActive({ strengths: items });
    setNewStrength("");
  };
  const removeStrength = (i) => {
    const items = (active.strengths || []).filter((_, idx) => idx !== i);
    updateActive({ strengths: items });
  };
  const addWeakness = () => {
    if (!newWeakness.trim()) return;
    const items = [...(active.weaknesses || []), newWeakness.trim()];
    updateActive({ weaknesses: items });
    setNewWeakness("");
  };
  const removeWeakness = (i) => {
    const items = (active.weaknesses || []).filter((_, idx) => idx !== i);
    updateActive({ weaknesses: items });
  };

  // 2026-02-09 — strengths/weaknesses moved from chip-list UI to paragraph
  // form. Storage stays as `List[str]` (each non-blank line = one item) so
  // the AI scouting-report generator and exports keep working. Family /
  // player view renders the items joined into a flowing prose paragraph.
  const itemsToText = (arr) => (arr || []).join("\n");
  const textToItems = (txt) =>
    (txt || "")
      .split(/\r?\n/)
      .map((s) => s.replace(/^[\s•\-–—]+/, "").trim())
      .filter(Boolean);
  const itemsToProse = (arr) => {
    const items = (arr || []).map((s) => s.trim()).filter(Boolean);
    if (!items.length) return "";
    return items
      .map((s) => {
        let t = s;
        if (t.length) t = t[0].toUpperCase() + t.slice(1);
        if (!/[.!?]$/.test(t)) t += ".";
        return t;
      })
      .join(" ");
  };

  const createEvaluation = async () => {
    try {
      const r = await api.post("/evaluations", { player_id: playerId });
      setEvals([r.data]);
      setActive(r.data);
      toast.success("Evaluation created");
    } catch (e) { toast.error("Failed"); }
  };

  if (loading) return <LoadingState />;

  if (!evals.length) {
    return (
      <div>
        <PlayerSubNav />
        <PageHeader title="Evaluation" subtitle="Trait grading, scouting report, and player narrative." />
        <EmptyState
          icon={ClipboardList}
          title="No evaluation yet"
          description={isAdmin ? "Create the first evaluation to begin grading this player." : "Your advisor hasn't published an evaluation yet."}
          action={isAdmin ? <Button data-testid="eval-create" className="cta-primary" onClick={createEvaluation}><Plus className="h-4 w-4" /> Create evaluation</Button> : null}
        />
      </div>
    );
  }

  const overall = active.overall_grade ?? active.overall_grade_recommended ?? 0;
  const recommended = active.overall_grade_recommended;

  return (
    <div className="max-w-[1500px] mx-auto">
      <PlayerSubNav />
      <PageHeader
        title="Evaluation"
        subtitle="Trait grading, scouting report, and player narrative."
        actions={
          isAdmin ? (
            <Button data-testid="eval-regenerate-button" disabled={generating} onClick={generate} className="cta-primary">
              <Sparkles className="h-4 w-4" /> {generating ? "Generating…" : (active?.ai_summary_md ? "Regenerate AI Report" : "Generate AI Report")}
            </Button>
          ) : null
        }
      />

      <div className="grid grid-cols-12 gap-5">
        {/* Header card: archetype + overall grade */}
        <GlassCard className="col-span-12 p-6 relative overflow-hidden" testId="evaluation-header-card">
          <div className="absolute -top-20 -right-20 h-72 w-72 rounded-full pointer-events-none" style={{ background: "radial-gradient(closest-side, rgba(34,211,238,0.18), transparent 70%)" }} />
          <div className="grid lg:grid-cols-3 gap-5 items-end">
            <div>
              <div className="text-3 text-xs uppercase tracking-wider">Player</div>
              <div className="text-1 text-2xl font-semibold mt-1">{player?.full_name}</div>
              <div className="text-2 text-sm">{player?.position} · {player?.school || "—"} · {player?.class_year || "—"}</div>
              <div className="mt-3">
                <Label className="text-3 text-[10px] uppercase tracking-wider">Archetype</Label>
                {isAdmin ? (
                  <Select value={active.archetype || ""} onValueChange={(v) => updateActive({ archetype: v })}>
                    <SelectTrigger data-testid="eval-archetype-select" className="bg-white/5 border-white/10 text-1 mt-1 w-full max-w-xs"><SelectValue placeholder="Select archetype" /></SelectTrigger>
                    <SelectContent className="glass-strong border-white/10 max-h-72">
                      {archetypes.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="text-1 mt-1">{active.archetype || "—"}</div>
                )}
              </div>
            </div>
            <div>
              <div className="text-3 text-xs uppercase tracking-wider">Recommended Grade</div>
              <div className="flex items-baseline gap-2 mt-1">
                <div className="text-5xl font-semibold text-1 font-mono" data-testid="eval-recommended-grade">{recommended != null ? recommended.toFixed(1) : "—"}</div>
                <div className="text-3 text-sm">/ 9.0</div>
              </div>
              {recommended != null && (
                <GradeLabelPill score={recommended} testId="eval-recommended-label" className="mt-2" />
              )}
              <div className="text-3 text-[11px] mt-2">50% General Traits · 50% Position Traits</div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <div className="text-3 text-xs uppercase tracking-wider">Overall Grade</div>
                {active.overall_grade_overridden && <StatusPill tone="amber">Override</StatusPill>}
              </div>
              <div className="flex items-baseline gap-2 mt-1">
                <div className="text-5xl font-semibold text-1 font-mono" data-testid="eval-overall-grade">{overall ? overall.toFixed(1) : "—"}</div>
                <div className="text-3 text-sm">/ 9.0</div>
              </div>
              {overall ? <GradeLabelPill score={overall} testId="eval-overall-label" className="mt-2" /> : null}
              {isAdmin && (
                <div className="flex items-center gap-2 mt-3">
                  <Input
                    data-testid="eval-overall-override"
                    type="number"
                    step="0.1"
                    min="1"
                    max="9"
                    value={overall || ""}
                    onChange={(e) => setOverallOverride(parseFloat(e.target.value || 0))}
                    className="bg-white/5 border-white/10 text-1 w-24 h-8 font-mono"
                  />
                  {active.overall_grade_overridden && (
                    <Button onClick={clearOverride} variant="ghost" size="sm" className="text-2 text-xs" data-testid="eval-clear-override">Use recommended</Button>
                  )}
                </div>
              )}
            </div>
          </div>
        </GlassCard>

        {/* Game grades — per-game trend */}
        <GameGradesCard playerId={playerId} />

        {/* Trait grading cards: General + Position */}
        {TRAIT_GROUPS.map((group) => {
          const Icon = group.icon;
          const items = active[group.key] || [];
          const avg = avgScored(items);
          // Family/player view stacks the two trait groups side-by-side on
          // lg+ to maximise horizontal density. Admin keeps full-width editor.
          return (
            <GlassCard
              key={group.key}
              className={isAdmin ? "col-span-12 p-6" : "col-span-12 lg:col-span-6 p-6"}
              testId={`eval-group-${group.key}`}
            >
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div className="flex items-start gap-3">
                  <div className="h-9 w-9 rounded-lg bg-white/5 ring-1 ring-white/10 grid place-items-center" style={{ color: group.color }}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div>
                    <h3 className="text-1 font-semibold">{group.label}</h3>
                    <div className="text-3 text-xs mt-0.5">{group.description}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-3 text-[10px] uppercase tracking-wider">Group Avg</div>
                  <div className="text-1 font-mono text-lg" data-testid={`eval-${group.key}-avg`}>
                    {avg != null ? avg.toFixed(1) : "—"}<span className="text-3 text-xs"> / 9.0</span>
                  </div>
                  {avg != null && <GradeLabelPill score={avg} className="mt-1.5" />}
                </div>
              </div>

              {/* Family/player simplified view: trait → grade → label rows.
                  Admin keeps the full editor below. */}
              {!isAdmin ? (
                <div className="mt-5 divide-y divide-white/5" data-testid={`eval-traits-simple-${group.key}`}>
                  {items.map((t, idx) => {
                    const g = gradeLabel(t.score);
                    const ungraded = g.value == null;
                    return (
                      <div
                        key={`${t.name}-${idx}`}
                        className="py-2.5 first:pt-0 last:pb-0 flex items-center gap-3"
                        data-testid={`eval-trait-row-${group.key}-${idx}`}
                      >
                        <div className="text-1 text-sm font-medium flex-1 min-w-0 truncate pr-2">{t.name}</div>
                        <div className={cn(
                          "font-mono text-xl font-semibold tabular-nums w-7 text-right shrink-0",
                          ungraded ? "text-3" : "text-1"
                        )}>
                          {ungraded ? "—" : Number(t.score).toFixed(0)}
                        </div>
                        <span className={cn(
                          "inline-flex items-center justify-center px-2 py-0.5 rounded-full ring-1 text-[11px] font-medium shrink-0 w-[88px] text-center",
                          GRADE_TONE_CLASS[g.tone]
                        )}>
                          {g.label}
                        </span>
                      </div>
                    );
                  })}
                  {items.length === 0 && <div className="text-2 text-sm py-4">No traits configured.</div>}
                </div>
              ) : (
              <div className="mt-5 divide-y divide-white/5">
                {items.map((t, idx) => (
                  <div key={`${t.name}-${idx}`} className="py-4 first:pt-0 last:pb-0 grid lg:grid-cols-12 gap-3 items-start">
                    <div className="lg:col-span-3 min-w-0">
                      <div className="text-1 text-sm font-medium">{t.name}</div>
                      <div className="text-3 text-[11px] mt-0.5 flex items-center gap-2">
                        <span>{t.score > 0 ? `Score ${Number(t.score).toFixed(1)}/9` : "Ungraded"}</span>
                        {t.score > 0 && <GradeLabelPill score={t.score} compact />}
                      </div>
                    </div>
                    <div className="lg:col-span-4">
                      <TraitBoxes
                        testId={`trait-${group.key}-${idx}`}
                        value={t.score || 0}
                        readOnly={!isAdmin}
                        onChange={(s) => setTraitScore(group.key, idx, s)}
                      />
                    </div>
                    <div className="lg:col-span-5">
                      {isAdmin ? (
                        <Textarea
                          data-testid={`trait-note-${group.key}-${idx}`}
                          value={t.note || ""}
                          onChange={(e) => setTraitNoteLocal(group.key, idx, e.target.value)}
                          onBlur={() => commitTraitNote(group.key)}
                          rows={2}
                          placeholder="Add a quick note for this trait…"
                          className="bg-white/5 border-white/10 text-1 text-sm min-h-[52px]"
                        />
                      ) : (
                        <div className="text-2 text-sm leading-relaxed glass p-2.5 min-h-[52px]" data-testid={`trait-note-readonly-${group.key}-${idx}`}>
                          {t.note?.trim() ? t.note : <span className="text-3 italic">No note</span>}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {items.length === 0 && <div className="text-2 text-sm py-4">No traits configured.</div>}
              </div>
              )}
            </GlassCard>
          );
        })}

        {/* One-liner + Strengths + Weaknesses */}
        <GlassCard className="col-span-12 p-6" testId="eval-narrative-card">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-3 text-xs uppercase tracking-wider">
              <Quote className="h-3.5 w-3.5" /> One-liner Headline
            </div>
            {isAdmin && (
              <Button
                size="sm"
                variant="ghost"
                disabled={headlineLoading}
                onClick={generateHeadline}
                data-testid="eval-headline-generate"
                className="h-7 text-cyan-300 hover:text-cyan-200 hover:bg-cyan-300/5"
              >
                <Sparkles className={`h-3.5 w-3.5 ${headlineLoading ? "animate-pulse" : ""}`} />
                {headlineLoading ? "Generating…" : (active.one_liner ? "Regenerate Headline" : "Auto-Generate Headline")}
              </Button>
            )}
          </div>

          {/* Family/player view: render as a prominent quote callout */}
          {!isAdmin && (
            active.one_liner ? (
              <div
                data-testid="eval-headline-readonly"
                className="mt-3 glass p-4 border-l-2 border-cyan-300/60"
              >
                <p className="text-1 text-base lg:text-lg font-medium leading-snug italic">
                  "{active.one_liner}"
                </p>
              </div>
            ) : (
              <p className="text-3 text-sm mt-3 italic">No headline available yet.</p>
            )
          )}

          {/* Admin view: editable + AI-generate button above already shown */}
          {isAdmin && (
            <Textarea
              data-testid="eval-one-liner"
              value={active.one_liner || ""}
              onChange={(e) => setActive({ ...active, one_liner: e.target.value })}
              onBlur={() => updateActive({ one_liner: active.one_liner || "" })}
              rows={2}
              placeholder="Sharp 12-22 word headline capturing this player's identity, or click Auto-Generate Headline above…"
              className="bg-white/5 border-white/10 text-1 mt-3"
            />
          )}

          <div className="mt-6 grid lg:grid-cols-2 gap-5">
            <div>
              <div className="flex items-center justify-between">
                <h4 className="text-1 font-semibold flex items-center gap-2">Strengths <StatusPill tone="emerald">{(active.strengths || []).length}</StatusPill></h4>
              </div>
              {isAdmin ? (
                <>
                  <Textarea
                    data-testid="eval-strengths-textarea"
                    rows={6}
                    value={itemsToText(active.strengths)}
                    onChange={(e) => {
                      const items = textToItems(e.target.value);
                      setActive({ ...active, strengths: items });
                    }}
                    onBlur={() => updateActive({ strengths: active.strengths || [] })}
                    placeholder={`One per line — e.g.\nElite pre-snap recognition\nLayered touch on three levels\nPlus arm strength outside the numbers`}
                    className="mt-3 bg-white/5 border-white/10 text-1 leading-relaxed"
                  />
                  <div className="text-3 text-[11px] italic mt-1.5">
                    Each line becomes one item. Saved as a paragraph for families.
                  </div>
                </>
              ) : (
                (active.strengths || []).length === 0 ? (
                  <p className="text-3 text-sm italic mt-3" data-testid="eval-strengths-empty">No strengths recorded yet.</p>
                ) : (
                  <p
                    data-testid="eval-strengths-prose"
                    className="text-2 text-[15px] leading-relaxed mt-3 glass p-4 border-l-2 border-emerald-300/50"
                  >
                    {itemsToProse(active.strengths)}
                  </p>
                )
              )}
            </div>
            <div>
              <div className="flex items-center justify-between">
                <h4 className="text-1 font-semibold flex items-center gap-2">Areas to Improve <StatusPill tone="amber">{(active.weaknesses || []).length}</StatusPill></h4>
              </div>
              {isAdmin ? (
                <>
                  <Textarea
                    data-testid="eval-weaknesses-textarea"
                    rows={6}
                    value={itemsToText(active.weaknesses)}
                    onChange={(e) => {
                      const items = textToItems(e.target.value);
                      setActive({ ...active, weaknesses: items });
                    }}
                    onBlur={() => updateActive({ weaknesses: active.weaknesses || [] })}
                    placeholder={`One per line — e.g.\nDurability under interior pressure\nDecision-making on second-reaction plays\nFootwork against stunts`}
                    className="mt-3 bg-white/5 border-white/10 text-1 leading-relaxed"
                  />
                  <div className="text-3 text-[11px] italic mt-1.5">
                    Each line becomes one item. Saved as a paragraph for families.
                  </div>
                </>
              ) : (
                (active.weaknesses || []).length === 0 ? (
                  <p className="text-3 text-sm italic mt-3" data-testid="eval-weaknesses-empty">No areas to improve recorded yet.</p>
                ) : (
                  <p
                    data-testid="eval-weaknesses-prose"
                    className="text-2 text-[15px] leading-relaxed mt-3 glass p-4 border-l-2 border-amber-300/50"
                  >
                    {itemsToProse(active.weaknesses)}
                  </p>
                )
              )}
            </div>
          </div>
        </GlassCard>

        {/* AI report markdown */}
        <GlassCard className="col-span-12 p-6" testId="eval-report-card">
          <div className="flex items-center gap-2 text-3 text-xs uppercase tracking-wider"><FileText className="h-3.5 w-3.5" /> AI SCOUTING REPORT</div>
          {active.ai_summary_md ? (
            <div className="markdown-body mt-3" data-testid="evaluation-report-markdown">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{active.ai_summary_md}</ReactMarkdown>
              <div className="text-3 text-[11px] mt-3">Generated: {new Date(active.ai_summary_generated_at).toLocaleString()}</div>
            </div>
          ) : (
            <div className="mt-3 text-2 text-sm">No AI report generated yet. {isAdmin && "Click 'Generate AI Report' to create one based on traits and notes."}</div>
          )}
        </GlassCard>

        {/* Notes log */}
        <GlassCard className="col-span-12 p-6" testId="eval-notes-card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-1 font-semibold">Scout notes log</h3>
            <span className="text-3 text-xs">{(active.notes || []).length} notes</span>
          </div>
          {isAdmin && (
            <div className="flex gap-2 mb-3">
              <Input
                data-testid="eval-note-input"
                placeholder="Add a quick scouting note…"
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addNote()}
                className="bg-white/5 border-white/10 text-1"
              />
              <Button data-testid="eval-note-add" onClick={addNote} className="cta-primary">Add</Button>
            </div>
          )}
          <div className="space-y-2">
            {(active.notes || []).slice().reverse().map((n) => (
              <div key={n.id} className="glass p-3">
                <div className="flex items-center justify-between">
                  <div className="text-1 text-sm font-medium">{n.author_name}</div>
                  <div className="text-3 text-[11px]">{new Date(n.created_at).toLocaleString()}</div>
                </div>
                <div className="text-2 text-sm mt-1 leading-relaxed">{n.text}</div>
              </div>
            ))}
            {(active.notes || []).length === 0 && <div className="text-2 text-sm">No notes yet.</div>}
          </div>
        </GlassCard>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small label pill for the canonical 1-9 grade scale.
// Default = full pill with label text. `compact` = single-letter / shorter.
// ---------------------------------------------------------------------------
function GradeLabelPill({ score, className = "", compact = false, testId }) {
  const g = gradeLabel(score);
  if (g.value == null) return null;
  return (
    <span
      data-testid={testId}
      className={cn(
        "inline-flex items-center justify-center rounded-full ring-1 font-medium",
        compact ? "px-1.5 py-0 text-[10px]" : "px-2.5 py-1 text-xs",
        GRADE_TONE_CLASS[g.tone],
        className
      )}
    >
      {g.label}
    </span>
  );
}
